import cn from 'classnames';
import { ReactNode, useMemo } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { MdLanguage } from 'react-icons/md';
import { Brand50 } from '../../utils/colors';
import { FullNavGroup as NavGroupType, NavGroupKey } from '../../utils/routing/location-constants';
import { Appearance, Background, Placement, Size } from '../designConstants';
import { IconButton } from '../IconButton';
import { InternalToolsMenu } from './InternalToolsMenu/InternalToolsMenu';
import { NAV_POPOVER_CONFIG } from './navbar-constants';
import { NavMenu } from './NavMenu';
import { useNavHoverOpen, useNavTopMenu } from './useNavBarPopover';

interface NavGroupProps {
  navGroup: NavGroupType;
}

export const NavGroup = ({ navGroup }: NavGroupProps): ReactNode => {
  const { key, prettyName, icon: Icon, items } = navGroup;
  const [showMenu, setShowMenu] = useNavTopMenu(key);
  const { hoverHandlers, consumeHoverOpen } = useNavHoverOpen(showMenu, () => setShowMenu(true));

  const overlay = useMemo(() => {
    if (key === NavGroupKey.INTERNAL_TOOLS) {
      if (!('subNavs' in items[0]) || !('tertiaryNavs' in items[0].subNavs[0])) {
        throw new Error('Malformed INTERNAL_TOOLS NavGroup');
      }

      return (
        <InternalToolsMenu
          prettyName={prettyName}
          sections={items[0]?.subNavs?.[0]?.tertiaryNavs}
          globalIcon={<MdLanguage />}
          isTertiary={false}
        />
      );
    }

    return (
      <Popover id={`${key}-popover`} className='nav-popover nav-group-popover'>
        <Popover.Header>
          <h3 className='nav-group-header'>
            <span className='nav-group-icon'>
              <Icon color={Brand50} />
            </span>
            <span className='nav-group-title'>{prettyName}</span>
          </h3>
        </Popover.Header>
        <Popover.Body>
          <NavMenu items={items} />
        </Popover.Body>
      </Popover>
    );
  }, [Icon, items, key, prettyName]);

  return (
    <OverlayTrigger
      placement={Placement.RIGHT_START}
      trigger='click'
      delay={300}
      show={showMenu}
      onToggle={nextShow => {
        if (consumeHoverOpen() && !nextShow) return;
        setShowMenu(nextShow);
      }}
      popperConfig={NAV_POPOVER_CONFIG}
      overlay={overlay}
    >
      <div data-testid={`nav-group-${key}`} className='nav-group-container' {...hoverHandlers}>
        <IconButton
          name={`${key}-nav-group-icon`}
          className={cn('nav-group-icon', { active: showMenu })}
          appearance={Appearance.EMPTY}
          background={Background.DARK}
          size={Size.LG}
        >
          <Icon color='white' />
        </IconButton>
        <label className='nav-group-icon-label' htmlFor={`${key}-nav-group-icon`}>
          {prettyName}
        </label>
      </div>
    </OverlayTrigger>
  );
};
